import { useEffect } from 'react';

const AUDIO_STATE_KEY = 'pravos_audio_state';
const EXPIRY_TIME = 5 * 60 * 1000; // 5 minutes

interface AudioState {
  trackIndex: number;
  timestamp: number;
}

/**
 * Syncs audio playback state across page refreshes
 *
 * Persists current track index to localStorage with a 5-minute expiry.
 * Helps maintain listening context when users refresh or return to the app.
 */
export function useAudioSessionSync(): void {
  // Clean up expired audio state on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(AUDIO_STATE_KEY);
      if (stored) {
        const state: AudioState = JSON.parse(stored);
        const now = Date.now();
        const age = now - state.timestamp;

        // Clear if expired (5 minutes old)
        if (age > EXPIRY_TIME) {
          localStorage.removeItem(AUDIO_STATE_KEY);
        }
      }
    } catch (error) {
      console.error('Failed to load audio state:', error);
      localStorage.removeItem(AUDIO_STATE_KEY);
    }
  }, []);
}

/**
 * Save current track index to localStorage
 */
export function saveAudioState(trackIndex: number): void {
  try {
    const state: AudioState = {
      trackIndex,
      timestamp: Date.now(),
    };
    localStorage.setItem(AUDIO_STATE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('Failed to save audio state:', error);
  }
}

/**
 * Load saved track index from localStorage (if not expired)
 */
export function loadAudioState(): number | null {
  try {
    const stored = localStorage.getItem(AUDIO_STATE_KEY);
    if (!stored) return null;

    const state: AudioState = JSON.parse(stored);
    const now = Date.now();
    const age = now - state.timestamp;

    // Return null if expired
    if (age > EXPIRY_TIME) {
      localStorage.removeItem(AUDIO_STATE_KEY);
      return null;
    }

    return state.trackIndex;
  } catch (error) {
    console.error('Failed to load audio state:', error);
    localStorage.removeItem(AUDIO_STATE_KEY);
    return null;
  }
}
